import { useEffect, useState, useCallback } from "react";
import { Lock, Delete, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "del"];

export const PinDots = ({ length, error }) => (
  <div className={`flex justify-center gap-4 ${error ? "animate-[shake_0.4s]" : ""}`} data-testid="pin-dots">
    {[0, 1, 2, 3].map((i) => (
      <span key={i} className={`w-3.5 h-3.5 rounded-full border transition-colors duration-200 ${i < length ? "bg-[#E5C158] border-[#E5C158]" : "border-[#E5C158]/40"} ${error ? "border-red-400" : ""}`} />
    ))}
  </div>
);

const ResetWithPassword = ({ onDone }) => {
  const { t } = useI18n();
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/auth/pin/reset", { password: pw });
      onDone(data);
    } catch (e2) {
      setErr(formatApiError(e2));
    }
  };
  return (
    <form onSubmit={submit} className="mt-6 space-y-3" data-testid="pin-reset-form">
      <p className="text-xs text-[#8E9BB4]">{t("pin_reset_desc")}</p>
      <input data-testid="pin-reset-password-input" type="password" className="input-navy" placeholder={t("auth_password")} value={pw} onChange={(e) => setPw(e.target.value)} />
      {err && <p className="text-xs text-red-400" data-testid="pin-reset-error">{err}</p>}
      <button data-testid="pin-reset-submit-btn" className="btn-gold w-full py-3 rounded-full text-sm">{t("pin_reset_btn")}</button>
    </form>
  );
};

export const LockScreen = () => {
  const { mergeUser, logout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);

  const submit = useCallback(async (code) => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/pin/verify", { pin: code });
      mergeUser(data);
    } catch (e) {
      setError(formatApiError(e));
      setPin("");
    } finally {
      setBusy(false);
    }
  }, [mergeUser]);

  const press = useCallback((k) => {
    if (busy) return;
    setError("");
    setPin((p) => {
      if (k === "del") return p.slice(0, -1);
      const next = (p + k).slice(0, 4);
      if (next.length === 4) submit(next);
      return next;
    });
  }, [busy, submit]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === "INPUT") return;
      if (/^\d$/.test(e.key)) press(e.key);
      if (e.key === "Backspace") press("del");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [press]);

  return (
    <div className="max-w-sm mx-auto px-5 py-16 sm:py-24 fade-up" data-testid="lock-screen">
      <div className="glass-card p-8 text-center">
        <span className="w-14 h-14 mx-auto rounded-full border border-[#E5C158]/40 grid place-items-center mb-6"><Lock className="text-[#E5C158]" size={22} /></span>
        <h1 className="font-serif text-3xl text-[#F4F6FA] mb-2">{t("pin_locked_title")}</h1>
        <p className="text-sm text-[#8E9BB4] mb-8">{t("pin_locked_sub")}</p>
        <PinDots length={pin.length} error={!!error} />
        <p className="h-5 mt-4 text-xs text-red-400" data-testid="pin-error">{error}</p>
        <div className="grid grid-cols-3 gap-3 mt-4">
          {KEYS.map((k) => k === "" ? <span key="empty" /> : (
            <button key={k} data-testid={`pin-key-${k}`} onClick={() => press(k)} disabled={busy}
              className="h-16 rounded-2xl border border-white/10 text-2xl font-serif text-[#F4F6FA] hover:border-[#E5C158]/50 hover:bg-[#E5C158]/5 active:scale-95 transition-[transform,background-color,border-color] duration-150 grid place-items-center">
              {k === "del" ? <Delete size={20} className="text-[#8E9BB4]" /> : k}
            </button>
          ))}
        </div>
        {busy && <Loader2 className="animate-spin text-[#E5C158] mx-auto mt-4" size={18} />}
        {forgot ? <ResetWithPassword onDone={mergeUser} /> : (
          <div className="mt-6 flex justify-between text-xs">
            <button data-testid="pin-forgot-btn" onClick={() => setForgot(true)} className="text-[#8E9BB4] hover:text-[#E5C158]">{t("pin_forgot")}</button>
            <button data-testid="pin-logout-btn" onClick={async () => { await logout(); nav("/"); }} className="text-[#8E9BB4] hover:text-[#E5C158]">{t("nav_logout")}</button>
          </div>
        )}
      </div>
    </div>
  );
};

export const useIdleLock = () => {
  const { user, lock } = useAuth();
  const active = !!(user && user.pin_enabled && user.pin_unlocked);
  const minutes = user?.pin_timeout || 15;
  useEffect(() => {
    if (!active) return;
    let last = Date.now();
    const bump = () => { last = Date.now(); };
    const events = ["mousemove", "keydown", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const id = setInterval(() => { if (Date.now() - last > minutes * 60000) lock(); }, 20000);
    return () => { clearInterval(id); events.forEach((e) => window.removeEventListener(e, bump)); };
  }, [active, lock, minutes]);
};
